import React, {useState} from 'react';
import * as XLSX from 'xlsx';
import {requestDashboard} from '../../../api/api';
import {CustomerReport} from '../../dashboard/types';

interface ReportsTabProps {
  apiBase: string;
  token: string;
}

const isoDate = (date: Date) => date.toISOString().slice(0, 10);
const formatDate = (value: string) => {
  const [year, month, day] = value.slice(0, 10).split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('en-US', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
};
const formatDateTime = (value: string) => new Date(value).toLocaleString('en-US', {
  day: '2-digit', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
});

export const ReportsTab: React.FC<ReportsTabProps> = ({apiBase, token}) => {
  const now = new Date();
  const [startDate, setStartDate] = useState(isoDate(new Date(now.getFullYear(), now.getMonth(), 1)));
  const [endDate, setEndDate] = useState(isoDate(now));
  const [report, setReport] = useState<CustomerReport | null>(null);
  const [recurringOnly, setRecurringOnly] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const rows = (report?.customers || []).filter((customer) => !recurringOnly || customer.isRecurring);
  const exportRows = rows.map((customer) => ({
    Customer: customer.name,
    Email: customer.email,
    Phone: customer.phone || '',
    Reservations: customer.totalReservations,
    Guests: customer.totalGuests,
    Completed: customer.completedReservations,
    Cancelled: customer.cancelledReservations,
    'Amount Paid': customer.totalPaid,
    'First Reservation': formatDateTime(customer.firstReservationAt),
    'Last Reservation': formatDateTime(customer.lastReservationAt),
    Recurring: customer.isRecurring ? 'Yes' : 'No',
  }));

  const loadReport = async (event: React.FormEvent) => {
    event.preventDefault();
    if (startDate > endDate) {
      setError('Start date must be on or before end date.');
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      const data = await requestDashboard<CustomerReport>({
        apiBase,
        token,
        path: `/reservations/reports/customers?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`,
        withAuth: true,
      });
      setReport(data);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to load the report.');
    } finally {
      setIsLoading(false);
    }
  };

  const exportExcel = () => {
    const sheet = XLSX.utils.json_to_sheet(exportRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, 'Customers');
    XLSX.writeFile(workbook, `customer-report-${startDate}-to-${endDate}.xlsx`);
  };

  const exportPdf = async () => {
    const {jsPDF} = await import('jspdf');
    const pdf = new jsPDF({orientation: 'landscape'});
    pdf.setFontSize(16);
    pdf.text('Customer Reservation Report', 14, 15);
    pdf.setFontSize(9);
    pdf.text(`${formatDate(startDate)} to ${formatDate(endDate)} | ${rows.length} customers`, 14, 21);
    let y = 30;
    const headings = ['Customer', 'Email', 'Reservations', 'Guests', 'Paid', 'First', 'Last', 'Recurring'];
    const x = [14, 58, 128, 158, 181, 210, 239, 268];
    pdf.setFont('helvetica', 'bold');
    headings.forEach((heading, index) => pdf.text(heading, x[index], y));
    pdf.setFont('helvetica', 'normal');
    y += 6;
    rows.forEach((customer) => {
      if (y > 195) {
        pdf.addPage('a4', 'landscape');
        y = 15;
      }
      const values = [
        customer.name.slice(0, 22), customer.email.slice(0, 34), String(customer.totalReservations),
        String(customer.totalGuests), customer.totalPaid.toFixed(2), formatDateTime(customer.firstReservationAt),
        formatDateTime(customer.lastReservationAt), customer.isRecurring ? 'Yes' : 'No',
      ];
      values.forEach((value, index) => pdf.text(value, x[index], y));
      y += 6;
    });
    pdf.save(`customer-report-${startDate}-to-${endDate}.pdf`);
  };

  return <section className='reports-section'>
    <div className='reports-heading'>
      <div><p className='eyebrow'>Customer insights</p><h2>Reservation Reports</h2></div>
      {report && <div className='report-export-actions'>
        <button type='button' onClick={exportExcel} disabled={!rows.length}>Export Excel</button>
        <button type='button' onClick={exportPdf} disabled={!rows.length}>Export PDF</button>
      </div>}
    </div>
    <form className='report-filters' onSubmit={loadReport}>
      <label>From<input type='date' value={startDate} max={endDate} onChange={(event) => setStartDate(event.target.value)} required /></label>
      <label>To<input type='date' value={endDate} min={startDate} onChange={(event) => setEndDate(event.target.value)} required /></label>
      <button type='submit' disabled={isLoading}>{isLoading ? 'Loading…' : 'Generate Report'}</button>
    </form>
    {error && <p className='status error'>{error}</p>}
    {report && <>
      <div className='report-metrics'>
        <article><span>Customers</span><strong>{report.totalCustomers}</strong></article>
        <article><span>Recurring customers</span><strong>{report.recurringCustomers}</strong></article>
        <article><span>Reservations</span><strong>{report.totalReservations}</strong></article>
      </div>
      <div className='report-table-heading'>
        <label><input type='checkbox' checked={recurringOnly} onChange={(event) => setRecurringOnly(event.target.checked)} /> Recurring customers only</label>
        <span>{rows.length} customers shown</span>
      </div>
      <div className='table-card table-wrap'><table><thead><tr>
        <th>Customer</th><th>Contact</th><th>Reservations</th><th>Guests</th><th>Completed</th><th>Cancelled</th><th>Paid</th><th>First / Last</th><th>Recurring</th>
      </tr></thead><tbody>
        {rows.map((customer) => <tr key={customer.email}>
          <td>{customer.name}</td><td><div>{customer.email}</div><div>{customer.phone || '—'}</div></td>
          <td>{customer.totalReservations}</td><td>{customer.totalGuests}</td><td>{customer.completedReservations}</td><td>{customer.cancelledReservations}</td>
          <td>{customer.totalPaid.toFixed(2)}</td><td>{formatDateTime(customer.firstReservationAt)}<br />{formatDateTime(customer.lastReservationAt)}</td><td>{customer.isRecurring ? 'Yes' : 'No'}</td>
        </tr>)}
        {!rows.length && <tr><td colSpan={9} className='empty-row'>No customers found for this report.</td></tr>}
      </tbody></table></div>
    </>}
  </section>;
};
